import React, { useContext } from 'react';
import { Row, Col, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CurrencyContext } from '../../utils/currencyContext';
import { EmailPlanContext } from '../../utils/emailPlanContext';
import getCurrencySign from '../../utils/getCurrencySign';
import { emailPlans } from '../../resources/static/index';
import cpanelLogo from '../../assets/img/cpanel.png';

function PlanColumns({ period }) {
    const { currentCurrency } = useContext(CurrencyContext);
    const { currentEmailPlan } = useContext(EmailPlanContext);
    const { i18n, t } = useTranslation();
    const roundToTwo = (num) => parseFloat(num.toFixed(2));
    const sign = getCurrencySign(currentCurrency);
    const dataCurrency = emailPlans[currentCurrency];
    const data = dataCurrency[`year_${period}`];
    const isNotFirstPeriod = period > 1;
    const yearCoefficient = 12 * period;
    const currentLanguage = i18n.language;
    const isFR = currentLanguage === 'fr';

    const getBillingCycle = (currentPlan) => {
        let currencyCode = 'annually';

        if (currentPlan === 'two') {
            currencyCode = 'biennially';
        } else if (currentPlan === 'three') {
            currencyCode = 'triennially';
        }

        return currencyCode;
    };

    const getPeriodDiscount = (per) => {
        let discount = null;

        if (per === 2) {
            discount = '10%';
        } else if (per === 3) {
            discount = '15%';
        }

        return discount;
    };

    const getCurrencyCode = (currency) => {
        let currencyCode = 1;

        if (currency === 'usd') {
            currencyCode = 4;
        } else if (currency === 'gbp') {
            currencyCode = 5;
        }

        return currencyCode;
    };

    return (
        <Row className="plan-row">
            <Col lg={4} md={12} xs={12}>
                <div className="plan-card">
                    <h2 className="plan-title">MailPro</h2>
                    <p className="plan-text">{t('email-page.plans-column.text')}</p>
                    <div className="plan-price">
                        <p className="price-info">
                            <span className="price-home">
                                {data.mailpro}
                                <sup>{sign}</sup>
                            </span>
                            /{t('titles.month')}
                        </p>
                        <p className="price-year">
                            {isNotFirstPeriod && (
                                <del>
                                    {roundToTwo(dataCurrency.year_1.mailpro * yearCoefficient)}
                                    {sign}
                                </del>
                            )}
                            &nbsp;
                            <strong>
                                {isNotFirstPeriod
                                    ? roundToTwo(data.mailpro * yearCoefficient)
                                    : roundToTwo(dataCurrency.year_1.mailpro * yearCoefficient)}
                                {sign}
                            </strong>
                            &nbsp;{t('titles.for')} {period}{' '}
                            {period > 1 ? t('titles.years') : t('titles.year')}
                        </p>
                    </div>
                    {isNotFirstPeriod && (
                        <div className="price-save">
                            {t('titles.save')} {getPeriodDiscount(period)}
                        </div>
                    )}
                    <div className="price-content" />
                    <div className="plan-feature">
                        <ul>
                            <li className="plan-count">
                                <p>
                                    <strong>5</strong> {t('email-page.plans-column.property-1')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>30{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                    {t('email-page.plans-column.property-2')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>50</strong> {t('email-page.plans-column.property-3')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>10</strong> {t('email-page.plans-column.property-4')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-5')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.unlimited')}
                                    </strong>{' '}
                                    {!isFR ? t('email-page.plans-column.property-5') : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-6')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.weekly')}
                                    </strong>{' '}
                                    {!isFR ? t('email-page.plans-column.property-6') : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <img src={cpanelLogo} alt="pricing-icon" width="33px" /> Webmail
                                </p>
                            </li>
                        </ul>
                    </div>
                    <Nav className="common-nav-button">
                        <Nav.Link
                            className="navbar-brand"
                            href={`https://obambu.com/order/?pid=149&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentEmailPlan)}`}
                            rel="noopener noreferrer"
                        >
                            {t('buttons.order-now')}
                        </Nav.Link>
                    </Nav>
                </div>
            </Col>
            <Col lg={4} md={12} xs={12}>
                <div className="plan-card active">
                    <div className="badge-active">{t('titles.best-choice')}</div>
                    <h2 className="plan-title">
                        MailPro <span>Plus+</span>
                    </h2>
                    <p className="plan-text">{t('email-page.plans-column.text-2')}</p>
                    <div className="plan-price">
                        <p className="price-info">
                            <span className="price-home">
                                {data.mailpro_plus}
                                <sup>{sign}</sup>
                            </span>
                            /{t('titles.month')}
                        </p>
                        <p className="price-year">
                            {isNotFirstPeriod && (
                                <del>
                                    {roundToTwo(dataCurrency.year_1.mailpro_plus * yearCoefficient)}
                                    {sign}
                                </del>
                            )}
                            &nbsp;
                            <strong>
                                {isNotFirstPeriod
                                    ? roundToTwo(data.mailpro_plus * yearCoefficient)
                                    : roundToTwo(
                                          dataCurrency.year_1.mailpro_plus * yearCoefficient
                                      )}
                                {sign}
                            </strong>
                            &nbsp;{t('titles.for')} {period}{' '}
                            {period > 1 ? t('titles.years') : t('titles.year')}
                        </p>
                    </div>
                    {isNotFirstPeriod && (
                        <div className="price-save">
                            {t('titles.save')} {getPeriodDiscount(period)}
                        </div>
                    )}
                    <div className="price-content" />
                    <div className="plan-feature">
                        <ul>
                            <li className="plan-count">
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-1')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.unlimited')}
                                    </strong>{' '}
                                    {!isFR ? `${t('email-page.plans-column.property-1')} ` : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>100{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                    {t('email-page.plans-column.property-2')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-3')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.unlimited')}
                                    </strong>{' '}
                                    {!isFR ? `${t('email-page.plans-column.property-3')} ` : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-4')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.unlimited')}
                                    </strong>{' '}
                                    {!isFR ? `${t('email-page.plans-column.property-4')} ` : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-5')} ` : null}
                                    <strong>
                                        {t('web-hosting-page.plans-column.unlimited')}
                                    </strong>{' '}
                                    {!isFR ? t('email-page.plans-column.property-5') : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    {isFR ? `${t('email-page.plans-column.property-6')} ` : null}
                                    <strong>{t('web-hosting-page.plans-column.daily')}</strong>{' '}
                                    {!isFR ? t('email-page.plans-column.property-6') : null}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <img src={cpanelLogo} alt="pricing-icon" width="33px" /> Webmail
                                </p>
                            </li>
                        </ul>
                    </div>
                    <Nav className="common-nav-button">
                        <Nav.Link
                            className="navbar-brand"
                            href={`https://obambu.com/order/?pid=150&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentEmailPlan)}`}
                            rel="noopener noreferrer"
                        >
                            {t('buttons.order-now')}
                        </Nav.Link>
                    </Nav>
                </div>
            </Col>
        </Row>
    );
}

PlanColumns.propTypes = {
    period: PropTypes.number
};

PlanColumns.defaultProps = {
    period: 1
};

export default PlanColumns;
